// C.1 — батч для юнитов Синтетикса (v6 ID).
// Реализует простые триггеры/Order'ы. Сложная Mod-система
// (mod_played, ally_received_mod, mod_limit) — работает через старую
// инфраструктуру modifications.js на ID-картах, совместимых с v6.
//
// Реализовано в этом батче:
//   - turn_start: Fixer (heal 1 self) — уже в fikser.js
//   - turn_end: Torchok composite (1 damage random ally + random enemy) — уже в torchok.js
//   - Stimulyatorshchik composite Order
//   - Hirurg-mehanik (увеличение mod_limit цели) — стаб
//   - Konveyer tel — стаб (clone unit from discard)
//   - Loborganik / Narcolab / Tseh implantov / Laborant NII — generate_mod_on_target
//     стабы (Mod-система требует расширения)
//   - Deathrattle: Smertnik / Gasper — N damage hero_enemy.

import { registerImplementedAbility } from '../../warn-unimplemented.js';
import { applyDamage } from '../../../combat.js';
import { applyHeroDamage } from '../../../hero-damage.js';
import { applyPermanentBuff } from '../../../buffs.js';
import { healUnit } from '../../livlife/heal.js';
import { registerOnUnitDiedExtraHandler, registerOnTurnStartExtraHandler } from '../../deus/hooks.js';
import { onUnitDied } from '../../deus/index.js';
import { registerOnAttackTrigger } from '../../livlife/on-attack-triggers.js';
import { registerC1Order } from '../../global/c1/order-registry.js';
import { resurrectUnit } from '../../livlife/resurrect.js';
import { generateRandomMod } from './mod-factory.js';
import { Card } from '../../../card.js';
import { cards as cardsSyntetics } from '../../../../data/cards-syntetics.js';
// C.1: реестр mod_played-триггеров (Доктор Шрам, Бишор-лич, Имплантолог, Биоинженер).
import './mod-played-triggers.js';

// ============================================================
// === ORDERS                                                  ===
// ============================================================

registerC1Order('syn_stimulyatorshchik', (state, source) => {
  const hero = state.players?.[source.ownerId]?.hero;
  if (!hero) return { ok: false };
  hero.base.atk += 1; hero.current.atk += 1;
  applyDamage(hero, 2, { state, source });
  return { ok: true };
});
registerImplementedAbility('syn_stimulyatorshchik', 'order');

registerC1Order('syn_podryvnik', (state, source, opts) => {
  const t = opts?.target;
  if (!t || t.ownerId !== source.ownerId || !(t.isAlive && t.isAlive())) return { ok: false };
  const tAtk = t.current?.atk ?? 0;
  // БАГ-фикс (Этап 56.45): раньше applyDamage(t,999) только ставил hp<0, но НЕ
  // убирал юнит с поля и НЕ запускал его Посмертие (юнит «мёртв», но висит в
  // слоте до EOT-cleanup). Правильная смерть (как активка Рипера): removeFromField
  // + onUnitDied → Посмертие цели срабатывает, слот освобождается сразу.
  t.alive = false;
  state.players[source.ownerId].removeFromField(t);
  state.log?.add(`✗ ${t.name_ru} [${state.players[source.ownerId]?.name}] погибает (Подрывник)`);
  onUnitDied(state, {
    ownerIdx: source.ownerId, card: t,
    killer: null, killerIdx: null, killerWasCounter: false
  });
  // Урон Герою врага = atk жертвы (игнорирует Авангард — бьём Героя напрямую).
  const enemyHero = state.players?.[1 - source.ownerId]?.hero;
  if (enemyHero && tAtk > 0) applyDamage(enemyHero, tAtk, { state, source });
  return { ok: true };
});
registerImplementedAbility('syn_podryvnik', 'order');

// === syn_augmented_giant — Order: получает 2 случайных Мода (генерация) ===
// БАГ-фикс (Этап 56.44): раньше Order был только застаблен (registerImplemented
// без логики, комментарий ошибочно гласил «passive, не Order»). По данным карты
// у гиганта ДВЕ способности: Order (2 Мода себе) + пассивка (лимит Модов +1,
// в mods-batch.js). Пассивка ставит overloadThreshold=4, так что 2 Мода не дают Сбой.
registerC1Order('syn_augmented_giant', (state, source) => {
  if (!source) return { ok: false };
  generateRandomMod(state, source.ownerId, { destination: 'attach', target: source });
  generateRandomMod(state, source.ownerId, { destination: 'attach', target: source });
  return { ok: true };
});
registerImplementedAbility('syn_augmented_giant', 'order');

// === syn_diler — Order: 1 случайный Мод в руку ===
registerC1Order('syn_diler', (state, source) => {
  generateRandomMod(state, source.ownerId, { destination: 'hand' });
  return { ok: true };
});
registerImplementedAbility('syn_diler', 'order');

// === syn_glavar_bandy — Order: summon Дилера на свободный слот ===
registerC1Order('syn_glavar_bandy', (state, source) => {
  const p = state.players?.[source.ownerId];
  if (!p?.field) return { ok: false };
  for (let l = 0; l < 2; l++) {
    const row = p.field[l];
    for (let s = 0; s < row.length; s++) {
      if (row[s] === null) {
        // Импорт summonUnit (поздний — через dynamic).
        import('../../global/summon.js').then(mod => {
          mod.summonUnit(state, source.ownerId, 'syn_diler', l, s);
        });
        return { ok: true };
      }
    }
  }
  return { ok: false, reason: 'no_free_slot' };
});
registerImplementedAbility('syn_glavar_bandy', 'order');

// === syn_hirurg_mehanik — Order: +1 mod_limit (overloadThreshold) выбранному союзнику ===
registerC1Order('syn_hirurg_mehanik', (state, source, opts) => {
  const t = opts?.target;
  if (!t || t.ownerId !== source.ownerId) return { ok: false };
  t.overloadThreshold = (t.overloadThreshold || 3) + 1;
  state.log?.add(`🔧 Хирург-механик: ${t.name_ru} +1 mod_limit (overload=${t.overloadThreshold})`);
  return { ok: true };
});
registerImplementedAbility('syn_hirurg_mehanik', 'order');

// === syn_perekup — Order: draw_from_discard 1 ===
// Берём случайную карту из своего сброса в руку.
registerC1Order('syn_perekup', (state, source) => {
  const p = state.players?.[source.ownerId];
  if (!p?.discard?.length) return { ok: false, reason: 'empty_discard' };
  if (p.hand.length >= 10) return { ok: false, reason: 'hand_full' };
  const idx = state.rng?.int ? state.rng.int(p.discard.length) : 0;
  const [card] = p.discard.splice(idx, 1);
  p.hand.push(card);
  state.log?.add(`🛍 Перекуп: ${card.name_ru} из сброса в руку`);
  return { ok: true };
});
registerImplementedAbility('syn_perekup', 'order');

// === syn_shkurodyor — Order: уничтожить своего юнита + воскресить случайного из сброса ===
// БУГ.22:
//   (а) Раньше resurrect шёл через dynamic import().then(...) — Order
//       возвращался ДО воскрешения (async race). Заменили на static-импорт.
//   (б) Fallback: если цель не передана (sandbox без picker'а или бот без
//       эвристики) — берём своего юнита с минимумом HP. Иначе Order молча
//       падал с 'no_ally' и эффект терялся.
registerC1Order('syn_shkurodyor', (state, source, opts) => {
  const p = state.players[source.ownerId];
  let ally = opts?.target;
  if (!ally || ally.ownerId !== source.ownerId || !ally.isAlive || !ally.isAlive()) {
    const candidates = (p?.unitsOnField?.() || []).filter(
      (u) => u && u !== source && u.type === 'unit' && u.isAlive && u.isAlive()
    );
    if (candidates.length === 0) return { ok: false, reason: 'no_ally' };
    candidates.sort((a, b) => (a.current?.hp ?? 0) - (b.current?.hp ?? 0));
    ally = candidates[0];
    state.log?.add(`🔪 Шкуродёр: цель не выбрана — жертвуем «${ally.name_ru}» (мин. HP)`);
  }
  // БАГ-фикс (Этап 56.45): правильная смерть жертвы (как активка Рипера) —
  // removeFromField + onUnitDied: Посмертие жертвы срабатывает И слот
  // освобождается сразу (чтобы воскрешённый юнит мог встать на него).
  ally.alive = false;
  p.removeFromField(ally);
  state.log?.add(`✗ ${ally.name_ru} [${p.name}] погибает (Шкуродёр)`);
  onUnitDied(state, {
    ownerIdx: source.ownerId, card: ally,
    killer: null, killerIdx: null, killerWasCounter: false
  });
  // Воскрешение случайного юнита из своего сброса с base-статами.
  const discardUnits = (p.discard || []).filter(c => c?.type === 'unit');
  if (discardUnits.length === 0) return { ok: true, ally_destroyed: true };
  const rIdx = state.rng?.int ? state.rng.int(discardUnits.length) : 0;
  const tpl = discardUnits[rIdx];
  let slot = null;
  for (let l = 0; l < 2 && !slot; l++) {
    for (let s = 0; s < 4; s++) if (p.field[l][s] === null) { slot = { line: l, slot: s }; break; }
  }
  if (!slot) return { ok: true, ally_destroyed: true };
  resurrectUnit(state, tpl, source.ownerId, { line: slot.line, idx: slot.slot }, { hpMode: 'full_hp' });
  return { ok: true };
});
registerImplementedAbility('syn_shkurodyor', 'order');

// === syn_zazyvala — Order: look 3 Мода из колоды, attach 1 на выбранного союзника (cost 0) ===
// Упрощённая реализация для бота: берём случайный Мод из колоды (или генерируем
// если в колоде нет) и навешиваем на выбранную цель.
registerC1Order('syn_zazyvala', (state, source, opts) => {
  const t = opts?.target;
  if (!t || t.ownerId !== source.ownerId) return { ok: false, reason: 'no_target' };
  const p = state.players[source.ownerId];
  // Возьмём 3 случайных Мода из колоды.
  const modsInDeck = (p.deck || []).filter(c => c?.type === 'mod');
  if (modsInDeck.length > 0) {
    const rIdx = state.rng?.int ? state.rng.int(modsInDeck.length) : 0;
    const pick = modsInDeck[rIdx];
    const dIdx = p.deck.indexOf(pick);
    if (dIdx >= 0) p.deck.splice(dIdx, 1);
    // Attach без isPlayed (не считается mod_played).
    import('../modifications.js').then(mod => {
      mod.attachMod(state, t, { id: pick.id, name_ru: pick.name_ru }, { isPlayed: false });
    });
  } else {
    // Нет Мода в колоде — генерируем случайный.
    generateRandomMod(state, source.ownerId, { destination: 'attach', target: t });
  }
  return { ok: true };
});
registerImplementedAbility('syn_zazyvala', 'order');

// ============================================================
// === TURN_START mod-generation triggers                      ===
// ============================================================
// Конвейер тел: turn_start, копия случайного юнита из сброса на свободный
//                слот, со stats:1/0/1 (упрощённо без модификаций/триггеров).
// Лаборант НИИ: turn_start, draw random mod из колоды + attach на ally_choose.
// Нарколаборатория: turn_start (чётный), 1 random mod на ally_random.
// Цех имплантов: turn_start (чётный), 1 random mod на ally_random.
// Лоборганик: turn_start (чётный), 1 random mod на ВСЕХ союзников без модов.

registerOnTurnStartExtraHandler((state) => {
  if (!state?.players) return;
  const turn = state.currentTurn ?? 0;
  const isEvenTurn = turn % 2 === 0;
  for (let pIdx = 0; pIdx < state.players.length; pIdx++) {
    const p = state.players[pIdx];
    if (!p?.field) continue;
    for (const row of p.field) for (const u of row || []) {
      if (!u || !u.isAlive || !u.isAlive()) continue;
      if (u.current?.silenced) continue;
      // Ванильные копии (Конвейер тел) не запускают НИКАКИХ триггеров начала
      // хода — в т.ч. не клонируют сами себя и не генерируют Моды.
      if (u.noTriggers) continue;

      // --- Конвейер тел: копия случайного юнита из сброса.
      // По тексту карты (DSL stats:1/0/1|no_mods|no_triggers): копия — это
      // ВАНИЛЬНЫЙ болван 1/0/1, без Модов и без триггеров/посмертий.
      if (u.id === 'syn_konveyer_tel') {
        const discardUnits = (p.discard || []).filter(c => c?.type === 'unit');
        if (discardUnits.length === 0) continue;
        // Свободный слот.
        let slot = null;
        for (let l = 0; l < 2 && !slot; l++) {
          for (let s = 0; s < 4; s++) if (p.field[l][s] === null) { slot = { line: l, idx: s }; break; }
        }
        if (!slot) continue;
        const picked = discardUnits[state.rng?.int ? state.rng.int(discardUnits.length) : 0];
        // picked может быть МЁРТВЫМ инстансом с навешанными Модами и hp<=0 —
        // его статы копировать нельзя. Берём чистый шаблон и форсим 1/0/1.
        const sourceTpl = picked.template || picked;
        const copy = new Card(sourceTpl);
        copy.base.atk = 1; copy.base.armor = 0; copy.base.hp = 1;
        copy.current.atk = 1; copy.current.armor = 0; copy.current.hp = 1;
        copy.modifications = [];        // no_mods
        copy.overloadThreshold = 3;
        copy.noTriggers = true;         // no_triggers (посмертия/turn_start/on-attack)
        copy.spawnPassiveFired = true;  // не запускать spawn-passive на след. ходу
        copy.orderFiredAtSpawn = true;
        copy.ownerId = pIdx;
        copy.line = slot.line;
        copy.slot = slot.idx;
        copy.placedTurn = state.currentTurn;
        copy.revealed = true;
        copy.initiation = 0;
        p.field[slot.line][slot.idx] = copy;
        state.log?.add(`🧬 Конвейер тел [${p.name}]: копия ${sourceTpl.name_ru} (1/0/1) → л.${slot.line + 1} сл.${slot.idx + 1}`);
      }

      // --- Лаборант НИИ: 1 random Мод из колоды на случайного союзника.
      if (u.id === 'syn_laborant_nii') {
        const modsInDeck = (p.deck || []).filter(c => c?.type === 'mod');
        const allies = [];
        for (const r2 of p.field) for (const a of r2 || []) {
          if (a && a.isAlive && a.isAlive() && a !== u) allies.push(a);
        }
        if (allies.length === 0) continue;
        const ally = allies[state.rng?.int ? state.rng.int(allies.length) : 0];
        if (modsInDeck.length > 0) {
          const pick = modsInDeck[state.rng?.int ? state.rng.int(modsInDeck.length) : 0];
          const dIdx = p.deck.indexOf(pick);
          if (dIdx >= 0) p.deck.splice(dIdx, 1);
          import('../modifications.js').then(mod => {
            mod.attachMod(state, ally, { id: pick.id, name_ru: pick.name_ru }, { isPlayed: false });
          });
        } else {
          generateRandomMod(state, pIdx, { destination: 'attach', target: ally });
        }
      }

      // --- Цех имплантов: 1 Мод юниту с НАИБОЛЬШИМ числом Модов (чётный ход).
      // Концентрация на одном — билд чемпиона (выбор фаундера). Тай-брейк при
      // равенстве — случайный среди лидеров. Нарколаборатория (syn_narcolab) —
      // в legacy narkolaboratoriya.js, тут НЕ дублируем (см. тест T226).
      if (u.id === 'syn_tseh_implantov' && isEvenTurn) {
        const allies = [];
        for (const r2 of p.field) for (const a of r2 || []) {
          if (a && a.isAlive && a.isAlive() && a !== u) allies.push(a);
        }
        if (allies.length === 0) continue;
        const modCountOf = (a) => Array.isArray(a.modifications) ? a.modifications.length : 0;
        let maxMods = -1;
        for (const a of allies) { const mc = modCountOf(a); if (mc > maxMods) maxMods = mc; }
        const leaders = allies.filter(a => modCountOf(a) === maxMods);
        const ally = leaders[state.rng?.int ? state.rng.int(leaders.length) : 0];
        generateRandomMod(state, pIdx, { destination: 'attach', target: ally });
      }

      // --- Лоборганик: 1 random Мод на ВСЕХ союзников БЕЗ модов (чётный ход).
      if (u.id === 'syn_loborganik' && isEvenTurn) {
        for (const r2 of p.field) for (const a of r2 || []) {
          if (!a || !a.isAlive || !a.isAlive()) continue;
          if (a === u) continue;
          const modCount = Array.isArray(a.modifications) ? a.modifications.length : 0;
          if (modCount > 0) continue;
          generateRandomMod(state, pIdx, { destination: 'attach', target: a });
        }
      }
    }
  }
});
registerImplementedAbility('syn_konveyer_tel', 'trigger');
registerImplementedAbility('syn_laborant_nii', 'trigger');
registerImplementedAbility('syn_narcolab', 'trigger');
registerImplementedAbility('syn_tseh_implantov', 'trigger');
registerImplementedAbility('syn_loborganik', 'trigger');

// ============================================================
// === DEATHRATTLES                                            ===
// ============================================================
registerOnUnitDiedExtraHandler((state, payload) => {
  const dead = payload?.card;
  if (!dead) return;
  if (dead.current?.silenced) return;
  // БАГ-фикс: Посмертие урона Герою шло через applyDamage(hero) — урон проходил,
  // но БЕЗ лога, без hero-иммунитета/триггеров/проверки победы (фаундер не видел
  // эффекта). Теперь штатный hero-путь applyHeroDamage + явная лог-строка.
  // Значения по тексту v6: Смертник 2, Гаспер 3.
  const heroDmg = dead.id === 'syn_smertnik' ? 2 : (dead.id === 'syn_gasper' ? 3 : 0);
  if (heroDmg > 0) {
    const defIdx = 1 - payload.ownerIdx;
    const enemyHero = state.players?.[defIdx]?.hero;
    if (enemyHero && enemyHero.hp > 0) {
      applyHeroDamage(state, defIdx, heroDmg, { pierce: false });
      state.log?.add(`☠ Посмертие [${dead.name_ru}]: ${heroDmg} урона Герою врага`);
    }
  }
});
registerImplementedAbility('syn_smertnik', 'deathrattle');
registerImplementedAbility('syn_gasper', 'deathrattle');

// ============================================================
// === ON-ATTACK TRIGGERS                                      ===
// ============================================================

// БУГ.22: Налётчик — dsl_trigger1 extra_damage_on_attack value=1 при ударе
// по вражескому Герою. Раньше у карты был только warn-suppressing-stub без
// реальной регистрации on-attack — поэтому +1 урона по Герою не наносился.
// Реестр on-attack уже фильтрует по cardId и блокирует под Молчанием.
registerOnAttackTrigger('syn_naletchik', (state, attacker, target) => {
  if (!target || target.type !== 'hero') return;
  if (!target.isAlive || !target.isAlive()) return;
  applyDamage(target, 1, { state, source: attacker });
});

// ============================================================
// === STUB-регистрация для большинства триггеров (mod-system) ===
// ============================================================
for (const id of [
  'syn_augmented_giant', 'syn_bioenginer', 'syn_biomeh', 'syn_bishor_lich',
  'syn_chorny_rynok', 'syn_doctor_shram', 'syn_hirurg_bez_litsenzii',
  'syn_implant_addikt', 'syn_implantirovanny', 'syn_implantolog',
  'syn_implantoman', 'syn_kiberpsih', 'syn_konveyer_tel',
  'syn_lab_mutatsiy', 'syn_laborant_nii', 'syn_loborganik', 'syn_naletchik',
  'syn_podpolny_boets', 'syn_rast_lich',
  'syn_skvill_lich', 'syn_temny_netrunner',
  'syn_tseh_implantov', 'syn_uein_lich', 'syn_ulichny_naemnik'
]) {
  registerImplementedAbility(id, 'trigger');
}

// Auras
registerImplementedAbility('syn_glavar_bandy', 'aura');
registerImplementedAbility('syn_rast_lich', 'aura');

// Vanilla
registerImplementedAbility('syn_vyshibala', 'passive');
